// C:\MonProjet\BBW_DELEGUER\preprod\babyworld-backoffice\babyworld-backoffice\src\pages\rapports\index.js

import React, { useEffect, useState, useCallback, useMemo } from "react";
import { 
  Card, Col, Row, Table, DatePicker, Button, Spin, Empty, 
  message, Modal, List, Tag, Typography, Space, Tooltip,
  Collapse, Badge 
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import Filter from "../../components/filter/Filter";
import { getRapports } from "../../redux/slices/rapports/rapportsThunk";
import { columns } from "./columns";
import { getGovernments } from "../../redux/slices/governments/governmentsThunk";
import { getRegions } from "../../redux/slices/regions/regionsThunk"; 
import { getSpecialtiesForFilter } from "../../redux/slices/specialtys/specialtysThunk";
import { getDelegatesList } from "../../redux/slices/delegates/delegatesThunk";
import { getAllDoctorsForFilter } from "../../redux/slices/persons/personsThunk";
import { RoleEnum } from "../../_utils/roleEnum";
import { 
  DownloadOutlined, AuditOutlined, UserOutlined, 
  RocketOutlined, EnvironmentOutlined, StarFilled,
  CrownOutlined,
  WarningOutlined, CheckCircleOutlined, StopOutlined
} from '@ant-design/icons';
import { exportReports } from "../../services/reportService";
import axiosInstance from "../../services/axiosServicesVisit";
import DoctorHistoryModal from '../../components/DoctorHistoryModal';
import dayjs from "dayjs";

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

// --- CONFIGURATION DE LA SEGMENTATION RELATIONNELLE AVEC COULEURS DE LA MODALE ---
export const RELATIONSHIP_CONFIG = {
  PARTNER_ACTIVE: {
    label: "⭐ Partenaire Stratégique",
    tagColor: "gold",
    icon: <StarFilled />,
    bgColor: "#fffbe6",
    borderColor: "#fadb14",
    titleColor: "#856404",
    shadow: "0 4px 12px rgba(250, 219, 20, 0.18)"
  },
  PARTNER_AT_RISK: {
    label: "⚠️ Partenaire Sous Vigilance",
    tagColor: "warning",
    icon: <WarningOutlined />,
    bgColor: "#fff7e6",
    borderColor: "#fa8c16",
    titleColor: "#d46b08",
    shadow: "0 4px 12px rgba(250, 140, 22, 0.18)"
  },
  KEY_PRESCRIBER: {
    label: "🚀 Prescripteur Clé (A+)",
    tagColor: "success",
    icon: <RocketOutlined />,
    bgColor: "#f6ffed",
    borderColor: "#52c41a",
    titleColor: "#237804",
    shadow: "0 4px 12px rgba(82, 196, 26, 0.18)"
  },
  STANDARD: {
    label: "👤 Standard",
    tagColor: "default",
    icon: <UserOutlined />,
    bgColor: "#f0f5ff",
    borderColor: "#adc6ff",
    titleColor: "#002766",
    shadow: "none"
  },
  REFRACTORY: {
    label: "⛔ Non Réceptif",
    tagColor: "error",
    icon: <StopOutlined />,
    bgColor: "#fff1f0",
    borderColor: "#ff4d4f",
    titleColor: "#cf1322",
    shadow: "0 4px 12px rgba(255, 77, 79, 0.18)"
  }
};

const RapportsPage = () => {
  const dispatch = useDispatch();
  
  // --- DONNÉES UTILISATEUR ET PERMISSIONS ---
  const user = useSelector((state) => state.signin.user);
  const isSupervisor = user?.role?.id === RoleEnum.SUPERVISOR;

  // --- ÉTATS LOCAUX ---
  const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10 });
  const [filters, setFilters] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [newDate, setNewDate] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);

  // États pour la modale Historique Médecin
  const [doctorModalVisible, setDoctorModalVisible] = useState(false);
  const [selectedDoctorId, setSelectedDoctorId] = useState(null);

  const handleOpenDoctorHistory = (personId) => {
    setSelectedDoctorId(personId);
    setDoctorModalVisible(true);
  };

  // --- RÉCUPÉRATION DONNÉES REDUX ---
  const rapportsState = useSelector((state) => state.rapports?.rapports || {});
  const rawRapports = rapportsState.data?.data || [];
  const totalItems = rapportsState.data?.total || 0;

  const allDelegatesList = useSelector((state) => state.delegates?.delegatesList?.data?.data || []);
  const doctorsList = useSelector((state) => state.persons?.doctorsForFilter || []);
  const governmentsList = useSelector((state) => state.governments?.governments?.data || []);
  const specialtysList = useSelector((state) => state.specialtys?.specialtiesList || []);
  const regionsRaw = useSelector((state) => state.regions?.regions || {});
  const regionsList = regionsRaw.data || []; 

  // =========================================================================
  // ⚡ FILTRAGE : EXCLURE CONTRÔLES SUPERVISEURS ⚡
  // =========================================================================
  const rapports_data = useMemo(() => {
    return rawRapports;
  }, [rawRapports]);
  // =========================================================================

  const getCategoryTagColor = (cat) => {
    if (!cat) return 'default';
    const c = String(cat).toUpperCase();
    if (c === 'AKTIV') return 'blue';
    if (c === 'SANOSAN') return 'green';
    if (c === 'MATERNITE') return 'purple';
    return 'cyan';
  };

  const getDoctorRowClass = useCallback((record) => {
    const person = record?.person;
    if (!person) return '';

    let cat = person.relationshipCategory || person.prescriptionProfile;
    if (!cat && doctorsList && doctorsList.length > 0) {
      const found = doctorsList.find(d => d.id === person.id);
      if (found) {
        cat = found.relationshipCategory || found.prescriptionProfile;
      }
    }

    const raw = String(cat || '').toUpperCase().trim();

    // 1. NON RÉCEPTIF [C-] -> FOND ROUGE CLAIR
    if (raw === 'REFRACTORY' || raw.includes('REFRACT') || raw.includes('RÉCEPTIF') || raw.includes('RECEPTIF')) {
      return 'row-refractory-red';
    }

    // 2. PARTENAIRE SOUS VIGILANCE [A-] -> FOND JAUNE CLAIR
    if (raw === 'PARTNER_AT_RISK' || raw.includes('VIGILANCE')) {
      return 'row-partner-gold';
    }

    // 3. PRESCRIPTEUR CLÉ [B] -> FOND VERT CLAIR
    if (raw === 'KEY_PRESCRIBER' || raw.includes('A+') || raw.includes('PRESCRIPTEUR')) {
      return 'row-prescriber-green';
    }

    // 4. PARTENAIRE STRATÉGIQUE [B+] -> FOND JAUNE CLAIR
    const isPart = person.isPartner === true || person.isPartner === 'true' || person.isPartner === 1 || raw === 'PARTNER_ACTIVE';
    if (isPart) {
      return 'row-partner-gold';
    }

    return '';
  }, [doctorsList]);

  // Filtrer les délégués selon la gamme sélectionnée
  const filteredDelegatesList = useMemo(() => {
    if (!filters?.category) return allDelegatesList;
    return allDelegatesList.filter(d => d.category === filters.category);
  }, [allDelegatesList, filters?.category]);

  // --- INITIALISATION DES FILTRES ---
  useEffect(() => {
    if (user) {
      setFilters({
        search: "", 
        category: "",
        specialtyId: [], 
        governmentId: [], 
        regionId: [], 
        personId: [],
        delegateId: isSupervisor ? user.supervisedDelegateIds || [] : [],
        sampleId: [], 
        type: [], 
        dateInterval: [],
        isPartner: ""
      });
    }
  }, [user, isSupervisor]);

  // --- CHARGEMENT DES RÉFÉRENTIELS ---
  useEffect(() => {
    dispatch(getDelegatesList({ page: 1, itemsPerPage: 1000 }));
    dispatch(getGovernments({ page: 1, limit: 100 }));
    dispatch(getRegions({ page: 1, itemsPerPage: 1000 })); 
    dispatch(getSpecialtiesForFilter());
    dispatch(getAllDoctorsForFilter());
  }, [dispatch]);

  // --- RÉCUPÉRATION DES RAPPORTS AVEC CACHE BUSTER ---
  const fetchRapports = useCallback(() => {
    if (!filters) return;
    const { dateInterval, ...apiFilters } = filters;

    let delegatesToSend = filters.delegateId && filters.delegateId.length > 0 
      ? [...filters.delegateId] 
      : (isSupervisor ? (user?.supervisedDelegateIds || []) : []);

    if (filters.category) {
      let delegatesOfCategory = allDelegatesList
        .filter(d => d.category === filters.category)
        .map(d => d.id);

      if (isSupervisor && user?.supervisedDelegateIds?.length > 0) {
        delegatesOfCategory = delegatesOfCategory.filter(id => user.supervisedDelegateIds.includes(id));
      }

      if (filters.delegateId && filters.delegateId.length > 0) {
        delegatesToSend = delegatesToSend.filter(id => delegatesOfCategory.includes(id));
      } else {
        delegatesToSend = delegatesOfCategory.length > 0 ? delegatesOfCategory : [-1];
      }
    }

    const finalFilters = { 
        ...apiFilters, 
        delegateId: delegatesToSend.length > 0 ? delegatesToSend : undefined,
        dateInterval: dateInterval?.length === 2 ? dateInterval.join(',') : undefined,
        _t: Date.now()
    };

    dispatch(getRapports({ pageNumber: pagination.currentPage, limit: pagination.pageSize, filter: finalFilters }));
  }, [filters, pagination, dispatch, allDelegatesList, isSupervisor, user]);

  useEffect(() => { fetchRapports(); }, [fetchRapports]);

  // --- HANDLERS ---
  const handleFilterChange = (value, key) => {
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value };
      if (key === "delegateId" && value.length > 0) {
        newFilters.search = ""; 
      }
      if (key === "category") {
        newFilters.delegateId = [];
      }
      return newFilters;
    });
    setPagination(p => ({ ...p, currentPage: 1 }));
  };

  // ⚡ RÉCUPÉRATION COMPLÈTE DU PARCOURS AVEC STATUT RÉEL DU MÉDECIN ⚡
  

  // EXPORT CSV DU PARCOURS
  

  const createOptions = (data, labelKeys = ["name"], idKey = "id", appendCategory = false) => 
    Array.isArray(data) ? data.map(item => {
      let label = labelKeys.map(key => item[key]).filter(Boolean).join(' ');
      if (appendCategory && item.category) {
        label += ` (${item.category})`;
      }
      return { 
        id: item[idKey], 
        label: label, 
        value: item[idKey] 
      };
    }) : [];

  const optionsLists = [
    {
      name: "Gamme",
      placeholder: "Gamme / Catégorie",
      key: "category",
      multiple: false,
      options: [
        { label: "Toutes les gammes", value: "" },
        { label: "AKTIV", value: "AKTIV" },
        { label: "SANOSAN", value: "SANOSAN" },
        { label: "MATERNITE", value: "MATERNITE" }
      ]
    },
    { 
      key: "personId", placeholder: "Médecin / Client", multiple: true, 
      options: createOptions(doctorsList, ["firstname", "lastname"]),
      optionFilterProp: "label" 
    },
    { key: "specialtyId", placeholder: "Spécialités", multiple: true, options: createOptions(specialtysList) },
    { key: "governmentId", placeholder: "Gouvernorat", multiple: true, options: createOptions(governmentsList) },
    { 
      key: "regionId", 
      placeholder: "Région", 
      multiple: true, 
      options: createOptions(regionsList),
      optionFilterProp: "label", 
      showSearch: true 
    },
    { 
      key: "delegateId", 
      placeholder: "Délégué", 
      multiple: true, 
      options: createOptions(filteredDelegatesList, ["firstname", "lastname"], "id", true), 
      optionFilterProp: "label",
      showSearch: true 
    },
    { key: "type", placeholder: "Type", multiple: true, options: [ { id: "Médecin", label: "Médecin", value: "Médecin" }, { id: "Pharmacien", label: "Pharmacien", value: "Pharmacien" }, { id: "Grossiste", label: "Grossiste", value: "Grossiste" }] },
    {
      name: "Partenaire",
      placeholder: "Statut Partenaire",
      key: "isPartner",
      multiple: false,
      options: [
        { label: "Tous", value: "" },
        { label: "⭐ Partenaires", value: "true" },
        { label: "Standard", value: "false" }
      ]
    }
  ];

  const handleUpdateDate = async () => {
    if (!editingReport || !newDate) return;
    setUpdateLoading(true);
    try {
        await axiosInstance.patch(`/v1/reports/${editingReport.id}/date`, { date: newDate.format('YYYY-MM-DD') });
        message.success('Date mise à jour.');
        setIsModalVisible(false);
        fetchRapports();
    } catch (error) { message.error("Erreur mise à jour."); }
    finally { setUpdateLoading(false); }
  };

  const handleExportAll = async () => {
    if (!filters) return;
    setExporting(true);
    try {
      const { dateInterval, ...apiFilters } = filters;

      let delegatesToSend = filters.delegateId && filters.delegateId.length > 0 
        ? [...filters.delegateId] 
        : (isSupervisor ? (user?.supervisedDelegateIds || []) : []);

      if (filters.category) {
        let delegatesOfCategory = allDelegatesList
          .filter(d => d.category === filters.category)
          .map(d => d.id);

        if (isSupervisor && user?.supervisedDelegateIds?.length > 0) {
          delegatesOfCategory = delegatesOfCategory.filter(id => user.supervisedDelegateIds.includes(id));
        }

        if (filters.delegateId && filters.delegateId.length > 0) {
          delegatesToSend = delegatesToSend.filter(id => delegatesOfCategory.includes(id));
        } else {
          delegatesToSend = delegatesOfCategory.length > 0 ? delegatesOfCategory : [-1];
        }
      }

      const finalFilters = { 
          ...apiFilters, 
          delegateId: delegatesToSend.length > 0 ? delegatesToSend : undefined,
          dateInterval: dateInterval?.length === 2 ? dateInterval.join(',') : undefined 
      };
      
      const response = await exportReports(finalFilters);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `export_rapports_${dayjs().format('YYYY-MM-DD')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      message.success("Exportation réussie.");
    } catch (e) {
      message.error("Erreur exportation");
    } finally {
      setExporting(false);
    }
  };

  if (!user || !filters) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><Spin size="large" /></div>;

  return (
    <div className="tabled">
      <Row gutter={[24, 0]}>
        <Col xs="24" xl={24}>
          <Card bordered={false} className="criclebox tablespace mb-24" title="Liste des Rapports Délégués" />
          <Card bordered={false} className="criclebox tablespace mb-24">
            <div style={{ padding: '0 24px 24px' }}>
              <Filter 
                onSearchValue={(val) => handleFilterChange(val, "search")} 
                optionsLists={optionsLists} 
                data={filters} 
                onSelect={(val, key) => handleFilterChange(val, key)} 
              />
              <Row gutter={16} style={{ marginTop: 16 }} justify="space-between">
                <Col>
                  <RangePicker 
                    value={filters.dateInterval?.length === 2 ? [dayjs(filters.dateInterval[0]), dayjs(filters.dateInterval[1])] : null} 
                    onChange={(dates) => handleFilterChange(dates ? [dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')] : [], 'dateInterval')} 
                  />
                </Col>
                <Col>
                  <Space>
                    <Button icon={<DownloadOutlined />} onClick={handleExportAll} loading={exporting}>Exporter CSV</Button>
                    <Button onClick={() => setFilters({
                      search: "", 
                      category: "", 
                      specialtyId: [], 
                      governmentId: [], 
                      regionId: [], 
                      personId: [], 
                      delegateId: [], 
                      sampleId: [], 
                      type: [], 
                      dateInterval: [], 
                      isPartner: ""
                    })}>
                      Réinitialiser
                    </Button>
                  </Space>
                </Col>
              </Row>
            </div>
            <Table
              columns={columns((report) => { setEditingReport(report); setNewDate(dayjs(report.date)); setIsModalVisible(true); }, handleOpenDoctorHistory)}
              dataSource={rapports_data}
              loading={rapportsState.loading}
              rowKey="id"
              rowClassName={(record) => {
                let baseClass = (record.id % 2 === 0 ? 'table-row-light' : 'table-row-dark');
                const docClass = getDoctorRowClass(record);
                return docClass ? `${baseClass} ${docClass}` : baseClass;
              }}
              pagination={{ current: pagination.currentPage, pageSize: pagination.pageSize, total: totalItems, onChange: (p, s) => setPagination({ currentPage: p, pageSize: s }), showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} rapports` }}
              scroll={{ x: 'max-content' }}
            />

            <style>{`
                /* 1. Partenaires (Fond jaune) */
                .row-partner-gold td {
                  background-color: #fffbe6 !important;
                }
                .row-partner-gold:hover td {
                  background-color: #fff1b8 !important;
                }
                .row-partner-gold a {
                  color: #856404 !important;
                  font-weight: bold !important;
                }

                /* 2. Non Réceptif [C-] (Fond rouge) */
                .row-refractory-red td {
                  background-color: #fff1f0 !important;
                }
                .row-refractory-red:hover td {
                  background-color: #ffccc7 !important;
                }
                .row-refractory-red a {
                  color: #cf1322 !important;
                  font-weight: bold !important;
                }

                /* 3. Prescripteur [B] (Fond vert) */
                .row-prescriber-green td {
                  background-color: #f6ffed !important;
                }
                .row-prescriber-green:hover td {
                  background-color: #d9f7be !important;
                }
                .row-prescriber-green a {
                  color: #389e0d !important;
                  font-weight: bold !important;
                }
              `}</style>
          </Card>
        </Col>
      </Row>

      {/* ========================================================================= */}
      {/* ⚡ MODALE PARCOURS DE VISITE DU MÉDECIN AVEC COULEUR DYNAMIQUE DE PROFIL ⚡ */}
      {/* ========================================================================= */}
      <DoctorHistoryModal
        visible={doctorModalVisible}
        onClose={() => setDoctorModalVisible(false)}
        personId={selectedDoctorId}
      />

      <Modal title="Modifier la date" open={isModalVisible} onOk={handleUpdateDate} onCancel={() => setIsModalVisible(false)} confirmLoading={updateLoading}>
        <DatePicker value={newDate} onChange={setNewDate} format="YYYY-MM-DD" style={{ width: '100%' }} />
      </Modal>
    </div>
  );
};

export default RapportsPage;